import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  SimpleGrid,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import DateTime from "react-datetime";
import { FilterContainer } from "components/Tables/FilterContainer";
import { LOCALE_PT_BR } from "constants/date-time";
import SearchSelect from "components/SearchSelect/SearchSelect";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { PERMITTEES_TYPES } from "services/permitteesService";
import { PERMITTEES_INSPECTION_STATUS } from "services/permitteesService";
import { permitteesService } from "services/permitteesService";
import { permitteesPointsService } from "services/permitteesPointsService";
import { formatDocument, parseDateBrToISO } from "utils/utils";
import { plateMask, validateVehiclePlate } from "utils/forms-utils";

export const PermitteesFilter = (props) => {
  const { t } = useTranslation('common');
  const { onFilter, filterSnapshot } = props;
  const { isOpen, onToggle } = useDisclosure();
  const showToast = useCustomToast();
  const [plate, setPlate] = useState("");
  const [spot, setSpot] = useState("");
  const [square, setSquare] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [nameOwner, setNameOwner] = useState("");
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [permitteeType, setPermitteeType] = useState("");
  const [inspectionStatus, setInspectionStatus] = useState("");
  const [squares, setSquares] = useState({});
  const [isLoadingExport, setIsLoadingExport] = useState(false);

  const isStartDateInvalid = startDate ? startDate > new Date() : false;
  const isEndDateInvalid = startDate && endDate ? endDate <= startDate : false;
  const [plateError, setPlateError] = useState(null);
  const placeholderColor = useColorModeValue("gray.300", "gray.400");

  const handlePlateChange = (e) => {
    const maskedPlate = plateMask(e.target.value);
    setPlate(maskedPlate);
    if (plateError) setPlateError(null);
  };

  const onFilterHandler = () => {
    onFilter(getFilters());
  }

  const onClearHandler = () => {
    setPlate("");
    setSpot("");
    setSquare("");
    setNameOwner("");
    setCpfCnpj("")
    setEndDate("");
    setPermitteeType("");
    setStartDate("");
    setInspectionStatus("");
    setPlateError(null);

    onFilter({
      placa: null,
      id_ponto_permissionario: null,
      numero: null,
      cpf_cnpj_proprietario: null,
      data_inicial: null,
      data_final: null,
      tipo: null,
      nome_proprietario: null,
      status_vistoria: null,
    });
  };

  const getFilters = () => {
    return ({
      placa: plate,
      id_ponto_permissionario: square,
      numero: spot,
      cpf_cnpj_proprietario: cpfCnpj,
      data_inicial: startDate ? new Date(startDate).toLocaleDateString(LOCALE_PT_BR) : "",
      data_final: endDate ? new Date(endDate).toLocaleDateString(LOCALE_PT_BR) : "",
      tipo: permitteeType,
      nome_proprietario: nameOwner,
      status_vistoria: inspectionStatus,
    });
  };

  const handleExportData = async () => {
    try {
      setIsLoadingExport(true);
      const { data, headers } = await permitteesService.export(getFilters());
      const fileName = headers['content-disposition'].match(/filename="(.+)"/)[1];

      const url = URL.createObjectURL(new Blob([data]));
      Object.assign(document.createElement('a'), {
        href: url,
        download: fileName
      }).click();

      URL.revokeObjectURL(url);

    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingExport(false);
    }
  };

  const handleToggle = () => {
    if (isOpen) {
      setPlateError(null);
    }
    onToggle();
  };

  const fetchStands = async () => {
    try {
      const res = await permitteesPointsService.findMany();
      setSquares(res ?? []);
    } catch (error) {
      setSquares([]);
    }
  };

  useEffect(() => {
    fetchStands();
  }, []);

  useLayoutEffect(() => {
    if (filterSnapshot == null) {
      setPlate("");
      setSpot("");
      setSquare("");
      setNameOwner("");
      setCpfCnpj("");
      setEndDate("");
      setPermitteeType("");
      setStartDate("");
      setInspectionStatus("");
      return;
    }
    setPlate(filterSnapshot.placa ? String(filterSnapshot.placa) : "");
    setSpot(filterSnapshot.numero ? String(filterSnapshot.numero) : "");
    setSquare(
      filterSnapshot.id_ponto_permissionario != null && filterSnapshot.id_ponto_permissionario !== ""
        ? String(filterSnapshot.id_ponto_permissionario)
        : ""
    );
    setNameOwner(filterSnapshot.nome_proprietario ? String(filterSnapshot.nome_proprietario) : "");
    setCpfCnpj(filterSnapshot.cpf_cnpj_proprietario ? String(filterSnapshot.cpf_cnpj_proprietario) : "");
    setPermitteeType(filterSnapshot.tipo != null && filterSnapshot.tipo !== "" ? String(filterSnapshot.tipo) : "");
    setInspectionStatus(
      filterSnapshot.status_vistoria != null && filterSnapshot.status_vistoria !== ""
        ? String(filterSnapshot.status_vistoria)
        : ""
    );
    if (filterSnapshot.data_inicial) {
      const d = parseDateBrToISO(filterSnapshot.data_inicial);
      setStartDate(d && !isNaN(d.getTime()) ? d : "");
    } else {
      setStartDate("");
    }
    if (filterSnapshot.data_final) {
      const d = parseDateBrToISO(filterSnapshot.data_final);
      setEndDate(d && !isNaN(d.getTime()) ? d : "");
    } else {
      setEndDate("");
    }
  }, [filterSnapshot]);

  return (
    <FilterContainer
      w='125px'
      fontSize='sm'
      fontWeight='bold'
      variant="light"
      isOpen={isOpen}
      onToggle={handleToggle}
      onFilter={onFilterHandler}
      onClear={onClearHandler}
      isExportable={true}
      onExport={handleExportData}
    >
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} w="100%">
        <FormControl>
          <FormLabel>{t("permittees.filters.fields.type")}</FormLabel>
          <SearchSelect
            items={Object.entries(PERMITTEES_TYPES).map(([id, nome]) => ({
              id,
              nome,
            }))}
            name="types"
            valueField="id"
            displayField="nome"
            value={permitteeType}
            onChange={(e) => setPermitteeType(e.currentTarget.value)}
            placeholder={t("permittees.filters.placeholders.type")}
          />
        </FormControl>

        <FormControl>
          <FormLabel>{t("permittees.filters.fields.status-inspection")}</FormLabel>
          <SearchSelect
            items={Object.entries(PERMITTEES_INSPECTION_STATUS).map(
              ([id, nome]) => ({
                id,
                nome,
              })
            )}
            name="status-inspection"
            valueField="id"
            displayField="nome"
            value={inspectionStatus}
            onChange={(e) => setInspectionStatus(e.currentTarget.value)}
            placeholder={t("permittees.filters.placeholders.status-inspection")}
          />
        </FormControl>

        <FormControl>
          <FormLabel>{t("permittees.filters.fields.square")}</FormLabel>
          <SearchSelect
            id={"stand"}
            value={square}
            items={squares}
            displayField={["numero", "tipo", "logradouro"]}
            valueField="id"
            onChange={(e) => setSquare(e.currentTarget.value)}
            placeholder={t("permittees.filters.placeholders.square")}
          />
        </FormControl>

        <FormControl isInvalid={!!plateError}>
          <FormLabel>
            {t("permittees.filters.fields.plate")}
          </FormLabel>
          <Input
            fontSize="sm"
            placeholder={t("permittees.filters.placeholders.plate")}
            _placeholder={{ color: placeholderColor, fontSize: "sm" }}
            value={plate}
            onBlur={() => {
              const err = validateVehiclePlate(
                plate,
                t("process.filters.fields.plate")
              );
              setPlateError(err);
            }}
            onChange={handlePlateChange}
          />
          <FormErrorMessage>
            {plateError && t(plateError)}
          </FormErrorMessage>
        </FormControl>

        <FormControl>
          <FormLabel>{t("permittees.filters.fields.spot")}</FormLabel>
          <Input
            fontSize="sm"
            placeholder={t("permittees.filters.placeholders.spot")}
            _placeholder={{ color: placeholderColor, fontSize: "sm" }}
            value={spot}
            onChange={(e) => setSpot(e.currentTarget.value)}
          />
        </FormControl>

        <FormControl isInvalid={isStartDateInvalid}>
          <FormLabel>{t("system.labels.start-date")}</FormLabel>
          <DateTime
            locale={LOCALE_PT_BR}
            closeOnSelect
            timeFormat={false}
            inputProps={startDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
            renderInput={(props) => (
              <Input
                {...props}
                fontSize="sm"
                _placeholder={{ color: placeholderColor, fontSize: "sm" }}
              />
            )}
            onChange={(date) => setStartDate(date)}
          />
          <FormErrorMessage>
            {t("system.messages.errors.invalid-start-date")}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={isEndDateInvalid}>
          <FormLabel>{t("system.labels.end-date")}</FormLabel>
          <DateTime
            locale={LOCALE_PT_BR}
            closeOnSelect
            timeFormat={false}
            inputProps={endDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
            renderInput={(props) => (
              <Input
                {...props}
                fontSize="sm"
                _placeholder={{ color: placeholderColor, fontSize: "sm" }}
              />
            )}
            onChange={date => setEndDate(date)} />
          <FormErrorMessage>
            {t("system.messages.errors.invalid-end-date")}
          </FormErrorMessage>
        </FormControl>

        <FormControl>
          <FormLabel>{t("permittees.filters.fields.name-owner")}</FormLabel>
          <Input
            fontSize="sm"
            placeholder={t("permittees.filters.placeholders.name-owner")}
            _placeholder={{ color: placeholderColor, fontSize: "sm" }}
            value={nameOwner}
            onChange={(e) => setNameOwner(e.currentTarget.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>{t("permittees.filters.fields.cpf-cnpj")}</FormLabel>
          <Input
            fontSize="sm"
            placeholder={t("permittees.filters.placeholders.cpf-cnpj")}
            _placeholder={{ color: placeholderColor, fontSize: "sm" }}
            value={formatDocument(cpfCnpj)}
            onChange={(e) => setCpfCnpj(e.currentTarget.value)}
            maxLength="18"
          />
        </FormControl>
      </SimpleGrid>
    </FilterContainer>
  );
}